import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { LegalSexEnum } from '@app/enums';
import { Department } from '@app/models/company/department.model';
import { Office } from '@app/models/company/office.model';
import { Employee } from '@app/models/employee/employee.model';
import { BaseForm } from '@app/modules/forms/base.form';
import { ReportFilter } from '@app/modules/reporting/interfaces/report-filter.interface';
import { AuthService, NotifyService } from '@app/services';
import { Job } from '@models/company/job.model';
import { TimeTrackingDateFormatDashes } from '@time-tracking/meta/time-tracking.meta';
import moment, { Moment } from 'moment';

@Component({
    selector: 'app-compensation-report-filter-form',
    templateUrl: './compensation-report-filter.template.html',
})
export class CompensationReportFilterForm extends BaseForm implements OnInit {
    @Output() filterApplied = new EventEmitter<ReportFilter[]>();

    startDate: Moment = moment().startOf('year');
    endDate: Moment = moment().endOf('year');
    departments: Department[] = [];
    jobs: Job[] = [];
    managers: Employee[] = [];

    selectedDepartmentIds: number[] = [];
    selectedJobIds: number[] = [];

    officeLocations: Office[] = [];
    legalSexOptions = [
        { value: LegalSexEnum.Male, label: 'account.m' },
        { value: LegalSexEnum.Female, label: 'account.f' },
        { value: LegalSexEnum.Other, label: 'account.x' },
        { value: LegalSexEnum.PreferNotToSay, label: 'account.preferNotToSay' },
    ];

    employmentStatuses = ['terminated', 'onboarding', 'active'];

    selectedOfficeLocationIds: number[] = [];
    selectedManagerIds: number[] = [];
    selectedEmploymentStatusIds: number[] = [];
    selectedLegalSex: string[] = [];

    constructor(
        private auth: AuthService,
        private notify: NotifyService
    ) {
        super();
    }

    async ngOnInit(): Promise<void> {
        try {
            await Promise.all([
                this.loadDepartments(),
                this.loadJobs(),
                this.loadOfficeLocations(),
                this.loadManagers(),
            ]);
        } catch (err) {
            this.notify.error(err);
        }
    }

    get filters(): ReportFilter[] {
        return [
            {
                name: 'startAt',
                value: this.startDate.format(TimeTrackingDateFormatDashes),
            },
            {
                name: 'endAt',
                value: this.endDate.format(TimeTrackingDateFormatDashes),
            },
            {
                name: 'department_ids',
                value: this.selectedDepartmentIds,
            },
            {
                name: 'office_ids',
                value: this.selectedOfficeLocationIds,
            },
            {
                name: 'reports_to',
                value: this.selectedManagerIds,
            },
            {
                name: 'status',
                value: this.selectedEmploymentStatusIds,
            },
            {
                name: 'legal_sex',
                value: this.selectedLegalSex,
            },
        ];
    }

    async loadDepartments(): Promise<void> {
        const [departments] = await Department.param('company', this.auth.company.id).orderBy('name', 'ASC').all();
        this.departments = departments;
    }

    async loadJobs(): Promise<void> {
        const [jobs] = await Job.param('company', this.auth.company.id).all();
        this.jobs = jobs;
    }

    async loadOfficeLocations(): Promise<void> {
        const [locations] = await Office.param('company', this.auth.company.id).all();
        this.officeLocations = locations;
    }

    async loadManagers(): Promise<void> {
        const [managers] = await Employee.param('company', this.auth.company.id).where('managersOnly', true).all();
        this.managers = managers;
    }

    applyFilter(): void {
        this.filterApplied.emit(this.filters);
    }
}
